import React, { useState } from "react";

// Account-action dialogs shared by the Talent and Admins tabs of the admin
// dashboard: delete, the can't-delete → freeze-instead hand-off, password
// reset, and freeze. Presentational — each owns only its own form fields and
// reports the admin's choice through callbacks. Moved out of
// pages/AdminDashboard.tsx in the Phase C split.

export function AdminDeleteModal({ onCancel, onConfirm }: { onCancel: () => void; onConfirm: () => void }) {
  return (
    <div className="adm-modal-backdrop fade-in">
      <div className="adm-modal-box">
        <h3 className="adm-mb-8">Confirm Delete</h3>
        <p className="muted-text adm-mb-16 adm-fs-88">
          This action cannot be undone.
        </p>
        <div className="adm-modal-actions">
          <button className="adm-btn-ghost" onClick={onCancel}>
            Cancel
          </button>
          <button className="adm-btn-red" onClick={onConfirm}>
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export interface BlockedDelete {
  id: string;
  name: string;
  orderCount: number;
  payoutCount: number;
}

export function AdminBlockedDeleteModal({
  blockedDelete,
  onCancel,
  onFreezeInstead,
}: {
  blockedDelete: BlockedDelete;
  onCancel: () => void;
  onFreezeInstead: () => void;
}) {
  return (
    <div className="adm-modal-backdrop fade-in">
      <div className="adm-modal-box">
        <h3 className="adm-mb-8">Can&apos;t delete this talent</h3>
        <p className="muted-text adm-mb-16 adm-fs-88">
          <strong>{blockedDelete.name}</strong> has {blockedDelete.orderCount} request
          {blockedDelete.orderCount === 1 ? "" : "s"} and {blockedDelete.payoutCount} payout
          {blockedDelete.payoutCount === 1 ? "" : "s"} on record. Financial history can&apos;t
          be deleted — it would orphan those records. Freeze the account instead: it stops all
          activity and payouts, keeps the records intact, and can be undone anytime.
        </p>
        <div className="adm-modal-actions">
          <button className="adm-btn-ghost" onClick={onCancel}>
            Cancel
          </button>
          <button
            className="adm-btn-green"
            onClick={onFreezeInstead}
          >
            Freeze instead
          </button>
        </div>
      </div>
    </div>
  );
}

export function AdminPasswordModal({ onCancel, onSave }: { onCancel: () => void; onSave: (newPassword: string) => void }) {
  const [newPassword, setNewPassword] = useState("");
  return (
    <div className="adm-modal-backdrop fade-in">
      <div className="adm-modal-box">
        <h3 className="adm-mb-12">Change Password</h3>
        <input
          type="password"
          placeholder="New password..."
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          className="adm-w-full adm-mb-12 adm-fs-88" style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid var(--adm-line)" }}
        />
        <div className="adm-modal-actions">
          <button
            className="adm-btn-ghost"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button className="adm-btn-green" onClick={() => onSave(newPassword)}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

export interface FreezeChoice {
  freezeType: "temporary" | "permanent";
  freezeReason: string;
  freezeUntil: string;
}

export function AdminFreezeModal({
  userName,
  onCancel,
  onConfirm,
}: {
  userName?: string;
  onCancel: () => void;
  onConfirm: (choice: FreezeChoice) => void;
}) {
  // Fresh fields on every open — the old inline modal reset them by hand each
  // time a Freeze button (or "Freeze instead") was clicked.
  const [freezeType, setFreezeType] = useState<"temporary" | "permanent">("temporary");
  const [freezeReason, setFreezeReason] = useState("");
  const [freezeUntil, setFreezeUntil] = useState("");
  return (
    <div className="adm-modal-backdrop fade-in">
      <div className="adm-modal-box">
        <h3 className="adm-mb-4">Freeze Account</h3>
        <p className="muted-text adm-mb-16 adm-fs-85">
          {userName}
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div>
            <label className="kpi-label adm-block adm-mb-4">
              Freeze Type
            </label>
            <select
              className="adm-select adm-w-full"
              value={freezeType}
              onChange={(e) => setFreezeType(e.target.value as "temporary" | "permanent")}
            >
              <option value="temporary">Temporary</option>
              <option value="permanent">Permanent</option>
            </select>
          </div>
          {freezeType === "temporary" && (
            <div>
              <label className="kpi-label adm-block adm-mb-4">
                Freeze Until
              </label>
              <input
                type="date"
                className="adm-search adm-w-full"
                value={freezeUntil}
                onChange={(e) => setFreezeUntil(e.target.value)}
              />
            </div>
          )}
          <div>
            <label className="kpi-label adm-block adm-mb-4">
              Reason (optional)
            </label>
            <input
              className="adm-search adm-w-full"
              placeholder="e.g. Violation of terms..."
              value={freezeReason}
              onChange={(e) => setFreezeReason(e.target.value)}
            />
          </div>
        </div>
        <div className="adm-modal-actions">
          <button className="adm-btn-ghost" onClick={onCancel}>
            Cancel
          </button>
          <button className="adm-btn-red" onClick={() => onConfirm({ freezeType, freezeReason, freezeUntil })}>
            Freeze Account
          </button>
        </div>
      </div>
    </div>
  );
}
